import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Camera, MapPin, RefreshCw, CheckCircle2, Loader2, ArrowLeft, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ACTIVITY_META } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function MarkAttendance() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [branch, setBranch] = useState("");
  const [activity, setActivity] = useState("at_office");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState(null);
  const [camActive, setCamActive] = useState(false);
  const [camError, setCamError] = useState("");
  const [gps, setGps] = useState(null);
  const [gpsStatus, setGpsStatus] = useState("idle"); // idle, locating, ok, error
  const [gpsError, setGpsError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });

  const needsGps = ACTIVITY_META[activity]?.gps;
  const needsDesc = activity !== "at_office";
  const activeBranches = useMemo(() => (branches || []).filter((b) => b.active), [branches]);

  const startCamera = async () => {
    setCamError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      setCamActive(true);
    } catch (e) {
      setCamError("Camera access is required to mark attendance.");
      setCamActive(false);
    }
  };

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamActive(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  // Attach the camera stream once the <video> element is actually mounted
  // (it only renders after camActive is true, so attaching earlier misses it).
  useEffect(() => {
    if (camActive && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [camActive]);

  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 480;
    canvas.height = video.videoHeight || 640;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    setPhoto(canvas.toDataURL("image/jpeg", 0.8));
    stopCamera();
  };

  const retake = () => {
    setPhoto(null);
    startCamera();
  };

  const requestGps = useCallback(() => {
    setGpsStatus("locating");
    setGpsError("");
    if (!navigator.geolocation) {
      setGpsStatus("error");
      setGpsError("Location access is required to mark attendance.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGpsStatus("ok");
      },
      () => {
        setGpsStatus("error");
        setGpsError("Location access is required to mark attendance.");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, []);

  // request GPS only after field/client selected
  useEffect(() => {
    if (needsGps) {
      setGps(null);
      requestGps();
    } else {
      setGps(null);
      setGpsStatus("idle");
    }
  }, [needsGps, requestGps]);

  const canSubmit =
    branch && photo && (!needsGps || gpsStatus === "ok") && (!needsDesc || description.trim());

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        branch_id: branch,
        activity_type: activity,
        description,
        photo,
        latitude: needsGps ? gps?.latitude : null,
        longitude: needsGps ? gps?.longitude : null,
        accuracy: needsGps ? gps?.accuracy : null,
      };
      const { data } = await api.post("/attendance/mark", payload);
      toast.success(data.message || "Attendance marked!");
      navigate("/app");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 wp-fade-up">
      <div className="flex items-center gap-3">
        <button data-testid="back-button" onClick={() => navigate("/app")} className="p-2 -ml-2 text-slate-600">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-head font-extrabold text-slate-900">Mark Attendance</h1>
      </div>

      {/* Branch */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200 space-y-2">
        <label className="text-sm font-semibold text-slate-700">Select Branch</label>
        <Select value={branch} onValueChange={setBranch}>
          <SelectTrigger data-testid="branch-select" className="h-12"><SelectValue placeholder="Choose branch" /></SelectTrigger>
          <SelectContent>
            {activeBranches.map((b) => (
              <SelectItem key={b.id} value={b.id} data-testid={`branch-option-${b.code}`}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Activity */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200 space-y-3">
        <label className="text-sm font-semibold text-slate-700">Activity Type</label>
        <div className="grid grid-cols-1 gap-2">
          {Object.entries(ACTIVITY_META).map(([key, meta]) => (
            <button
              key={key}
              data-testid={`activity-${key}`}
              onClick={() => setActivity(key)}
              className={`text-left px-4 py-3 rounded-xl border font-medium transition-colors ${
                activity === key ? "bg-blue-600 text-white border-blue-600" : "bg-slate-50 text-slate-700 border-slate-200"
              }`}
            >
              {meta.label}
              {meta.gps && <span className="text-xs ml-2 opacity-70">· GPS required</span>}
            </button>
          ))}
        </div>
        {needsDesc && (
          <Textarea
            data-testid="description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (required)"
            className="mt-2"
          />
        )}
        {activity === "at_office" && (
          <Textarea
            data-testid="description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            className="mt-2"
          />
        )}
      </div>

      {/* Camera */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200 space-y-3">
        <label className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Camera className="h-4 w-4" /> Live Photo</label>
        <div className="relative rounded-xl overflow-hidden bg-slate-900 aspect-[3/4] max-h-80 mx-auto flex items-center justify-center">
          {photo ? (
            <img src={photo} alt="capture" className="w-full h-full object-cover" data-testid="captured-photo" />
          ) : camActive ? (
            <>
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              <div className="absolute inset-8 border-2 border-white/60 rounded-full" />
            </>
          ) : (
            <div className="text-center text-slate-400 px-6">
              <Camera className="h-10 w-10 mx-auto mb-2" />
              <p className="text-sm">Tap below to open your camera</p>
              {camError && <p className="text-red-400 text-sm mt-2" data-testid="camera-error">{camError}</p>}
            </div>
          )}
        </div>
        {photo ? (
          <Button data-testid="retake-photo-button" variant="outline" onClick={retake} className="w-full h-11">
            <RefreshCw className="h-4 w-4 mr-2" /> Retake Photo
          </Button>
        ) : camActive ? (
          <Button data-testid="capture-photo-button" onClick={capture} className="w-full h-11 bg-blue-600 hover:bg-blue-700">
            <Camera className="h-4 w-4 mr-2" /> Capture
          </Button>
        ) : (
          <Button data-testid="open-camera-button" onClick={startCamera} className="w-full h-11 bg-slate-900 hover:bg-slate-800">
            <Camera className="h-4 w-4 mr-2" /> Open Camera
          </Button>
        )}
      </div>

      {/* GPS */}
      {needsGps && (
        <div className="bg-white rounded-2xl p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-slate-700 flex items-center gap-2"><MapPin className="h-4 w-4" /> Location</label>
            {gpsStatus === "ok" && <span data-testid="gps-status-ok" className="text-xs font-semibold text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Verified</span>}
            {gpsStatus === "locating" && <span data-testid="gps-status-locating" className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="h-4 w-4 animate-spin" /> Locating…</span>}
            {gpsStatus === "error" && <span data-testid="gps-status-error" className="text-xs text-red-600">Failed</span>}
          </div>
          {gps && gpsStatus === "ok" && (
            <p className="text-xs font-mono text-slate-500 mt-2">{gps.latitude.toFixed(5)}, {gps.longitude.toFixed(5)} (±{Math.round(gps.accuracy)}m)</p>
          )}
          {gpsStatus === "error" && (
            <div className="mt-2">
              <p className="text-sm text-red-600" data-testid="gps-error">{gpsError}</p>
              <Button variant="outline" size="sm" onClick={requestGps} className="mt-2">Retry Location</Button>
            </div>
          )}
        </div>
      )}

      <Button
        data-testid="submit-attendance-button"
        onClick={submit}
        disabled={!canSubmit || submitting}
        className="w-full h-14 text-base font-head font-bold bg-blue-600 hover:bg-blue-700 disabled:opacity-40"
      >
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "MARK ATTENDANCE"}
      </Button>
    </div>
  );
}
